import React, { useState } from "react";
import "./Evidence.css";
import WordByWordAnimator from "./WordByWordAnimator";
import { FaUser, FaFileAlt, FaUserSecret, FaTimes } from "react-icons/fa";
import { useMediaQuery } from "@mui/material";

const Evidence = () => {
  const maxWidth1200 = useMediaQuery("(max-width: 1200px)");
  const minWidth1024 = useMediaQuery("(min-width: 1024px)");
  const isMediumScreen = maxWidth1200 && minWidth1024;
  const isMobileScreen = useMediaQuery("(max-width: 1024px)");

  const [coverLetterOpen, setCoverLetterOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);

  // Sample output. The applicant details are the founder's own real
// background, which he has consented to; the target employer is fictional
// on purpose. A real company named as the recipient of a cover letter we
// publish as marketing implies a relationship with them that we do not
// have. Keep the employer here invented.
  const coverLetterText = `Dear hiring manager at Meridian Health:

When I came across Meridian Health, I was immediately drawn to your mission of revolutionizing healthcare billing and solving real problems for your customers. It's clear that you have created a culture of passionate people who are driven by building high quality software and ensuring the customer experience is nothing short of exceptional.

Having completed a Bachelor's in Computer Science and Mathematics at Northeastern University as well as multiple internships, I am prepared to take on the responsibilities this position requires. I have spent the last four years developing as a software engineer through numerous personal projects and internships. I worked at Apple and Cisco building both front-end and back-end applications and features, handling everything from end-to-end user interaction to developing the data platforms for the features to function. At Apple, I accelerated developer feedback through automated code review and risk analysis tools powered by AI. At Cisco, I built an event publisher that allowed users to monitor network device statuses in real time, as well as a React application that reflected devices and their statuses. My experiences provided me with a strong foundation in Object-Oriented design, testing and software development best practices, as well as extensive experience in coding in languages such as Java, Python, Typescript, Dart, C, C++, JavaScript, and x86 Assembly, as well as AWS, React, Tensorflow/Keras, Firebase, Selenium, Git, and Datadog software. Furthermore, my studies at Northeastern have given me a strong understanding of algorithms and data structures, cybersecurity, and computer systems and networks.

Beyond my technical skills, I am also a strong communicator with a customer-first mentality. I appreciate the challenge of explaining complex ideas simply in-person and in writing, and I always prioritize the end user in my work. I'd love the opportunity to speak to you about how I can help Meridian Health scale effectively.

Thank you for your time and consideration. I look forward to hearing from you.

Sincerely,
Jake Writer`;

  const questionText = "Why are you interested in this position?";
  const answerText =
    "There are a few main reasons why I want to work for Meridian Health. The first is the company culture. From the outside, it seems like a very collaborative environment where everyone is encouraged to learn and grow. This is the kind of environment that I thrive in, and I believe it is essential for innovation and success. Second, the reputation of the company. I have heard nothing but good things about the quality of the employees and the work that is being done at Meridian Health. I want to work with and learn from the best, and I believe that I would have that opportunity here. Third, the job description itself sounds like a perfect fit for my skills and experience. I have experience with the technologies that Meridian Health uses, and I have a proven track record of problem-solving and creating high-quality software. Finally, the location. Boston is a great city with a thriving tech scene and I would love to live there again.\n";

  return (
    <div className="evidence-section">
      <div className="evidence-container">
        <div className="evidence-content">
          <div className="evidence-header">
            <h2 className="evidence-title">
              Our AI Generated Cover Letters/Responses
            </h2>
            <p className="evidence-subtitle">
              We use your profile and the job posting to write each cover letter
              and screening answer for that specific role.
            </p>
          </div>

          <div className="evidence-features">
            <div className="feature-grid">
              <div className="feature-item">
                <div className="feature-icon">
                  <FaUser />
                </div>
                <div>
                  <h3>Personalized Content</h3>
                  <p>
                    We tailor our cover letters to your profile, experience, and
                    the specific company and job description
                  </p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <FaFileAlt />
                </div>
                <div>
                  <h3>Professional Formatting</h3>
                  <p>Industry-standard structure and tone</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <FaUserSecret />
                </div>
                <div>
                  <h3>Written From Your Profile</h3>
                  <p>
                    Each letter draws on your experience and the posting, and
                    you can see and edit the answers we wrote for you.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isMobileScreen ? (
          <div className="evidence-demo-mobile">
            <button
              className="evidence-preview-card"
              onClick={() => setCoverLetterOpen(true)}
              aria-label="View cover letter example"
            >
              <div className="preview-card-left">
                <div className="preview-card-icon">
                  <FaFileAlt />
                </div>
                <div className="preview-card-text">
                  <h3>Cover Letter Example</h3>
                  <p>See how we craft personalized, job-specific cover letters</p>
                </div>
              </div>
              <span className="preview-card-arrow">→</span>
            </button>

            <button
              className="evidence-preview-card qa-preview"
              onClick={() => setQaOpen(true)}
              aria-label="View Q&A response example"
            >
              <div className="preview-card-left">
                <div className="preview-card-icon qa">
                  <FaUser />
                </div>
                <div className="preview-card-text">
                  <h3>Q&amp;A Response Example</h3>
                  <p>See how we answer application screening questions</p>
                </div>
              </div>
              <span className="preview-card-arrow">→</span>
            </button>
          </div>
        ) : (
          <div className="evidence-demo">
            <div className="cover-letter-container">
              <div className="paper-header">
                <div className="user-info">
                  <div className="user-avatar">
                    <FaUser size={20} color="#666" />
                  </div>
                  <div className="contact-details">
                    <span className="name">Jake Writer</span>
                    <span className="email">jakewriter@northeastern.edu</span>
                  </div>
                </div>
              </div>

              <div className="letter-content">
                <WordByWordAnimator text={coverLetterText} />
              </div>
            </div>

            <div className="qa-container">
              <div className="qa-header">
                <h3>Q&A Response Example</h3>
              </div>
              <div className="qa-content">
                <div className="question">
                  {!isMediumScreen ? <span className="qa-label">Q:</span> : null}
                  <p>{questionText}</p>
                </div>
                <div className="answer">
                  {!isMediumScreen ? <span className="qa-label">A:</span> : null}
                  <WordByWordAnimator text={answerText} delay={2000} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cover Letter Modal */}
      {coverLetterOpen && (
        <div
          className="evidence-modal-overlay"
          onClick={() => setCoverLetterOpen(false)}
        >
          <div
            className="evidence-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Cover letter example"
          >
            <div className="evidence-modal-header">
              <div className="evidence-modal-title">
                <div className="modal-title-icon">
                  <FaFileAlt />
                </div>
                <div>
                  <h3>Cover Letter Example</h3>
                  <p>AI-generated for Jake Writer → Meridian Health</p>
                </div>
              </div>
              <button
                className="evidence-modal-close"
                onClick={() => setCoverLetterOpen(false)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="evidence-modal-body">
              <div className="modal-user-header">
                <div className="modal-user-avatar">
                  <FaUser size={16} />
                </div>
                <div className="contact-details">
                  <span className="name">Jake Writer</span>
                  <span className="email">jakewriter@northeastern.edu</span>
                </div>
              </div>
              <div className="modal-letter-content">
                <WordByWordAnimator text={coverLetterText} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Q&A Modal */}
      {qaOpen && (
        <div
          className="evidence-modal-overlay"
          onClick={() => setQaOpen(false)}
        >
          <div
            className="evidence-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Q&A response example"
          >
            <div className="evidence-modal-header">
              <div className="evidence-modal-title">
                <div className="modal-title-icon qa">
                  <FaUser />
                </div>
                <div>
                  <h3>Q&amp;A Response Example</h3>
                  <p>AI-generated screening answer for Meridian Health</p>
                </div>
              </div>
              <button
                className="evidence-modal-close"
                onClick={() => setQaOpen(false)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="evidence-modal-body">
              <div className="modal-qa-content">
                <div className="modal-question">
                  <span className="modal-qa-label q">Q</span>
                  <p>{questionText}</p>
                </div>
                <div className="modal-answer">
                  <span className="modal-qa-label a">A</span>
                  <WordByWordAnimator text={answerText} delay={500} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Evidence;
